import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject, signal} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {of} from 'rxjs';
import {catchError, finalize, take} from 'rxjs/operators';
import {MaterialModule} from '../../../material.module';
import {ConnectRun, ConnectWorkflowsService} from '../../../services/connect/connect-workflows.service';
import {ToastService} from '../../../services/layout/toast.service';

@Component({
    selector: 'app-connect-runs',
    standalone: true,
    imports: [CommonModule, RouterModule, MaterialModule],
    templateUrl: './connect-runs.component.html',
    styleUrls: ['../connect-shared.scss', './connect-runs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {class: 'connect-theme'},
})
export class ConnectRunsComponent implements OnInit {
    private readonly workflowsService = inject(ConnectWorkflowsService);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly filterWorkflowId = signal<string>('');
    readonly loading = signal<boolean>(false);
    readonly error = signal<string | null>(null);
    readonly runs = signal<ConnectRun[]>([]);

    readonly displayedColumns = ['id', 'workflow', 'status', 'duration', 'created', 'actions'] as const;

    ngOnInit(): void {
        this.loadRuns();
    }

    loadRuns(): void {
        if (this.loading()) return;
        this.loading.set(true);
        this.error.set(null);
        const wfId = this.filterWorkflowId().trim();

        this.workflowsService
            .listRuns(wfId ? wfId : undefined)
            .pipe(
                take(1),
                catchError(err => {
                    console.error('Failed to load runs', err);
                    this.error.set('Could not load runs yet.');
                    return of<ConnectRun[]>([]);
                }),
                finalize(() => {
                    this.loading.set(false);
                    this.cdr.markForCheck();
                }),
            )
            .subscribe(runs => {
                this.runs.set(Array.isArray(runs) ? runs : []);
                this.cdr.markForCheck();
            });
    }

    openRun(run: ConnectRun): void {
        this.router.navigate(['/connect/workflows', run.workflow_id, 'run', run.id]);
    }

    rerun(run: ConnectRun): void {
        this.workflowsService
            .rerun(run.id)
            .pipe(
                take(1),
                catchError(err => {
                    const msg = (err as { message?: string } | null)?.message || 'Failed to rerun.';
                    this.toastService.showError(msg, 4000);
                    return of(null);
                }),
            )
            .subscribe(result => {
                if (!result) return;
                const nextRunId = String(result.runId || '').trim();
                this.toastService.showSuccess('Rerun queued.', 2500);
                if (nextRunId) {
                    this.router.navigate(['/connect/workflows', run.workflow_id, 'run', nextRunId]);
                    return;
                }
                this.loadRuns();
            });
    }

    humanStatus(status: string | undefined | null): string {
        const s = String(status || '').toLowerCase();
        if (s === 'succeeded') return 'Succeeded';
        if (s === 'failed') return 'Failed';
        if (s === 'queued') return 'Queued';
        if (s === 'running') return 'Running';
        return status || '-';
    }

    statusClass(status: string | undefined | null): string {
        const s = String(status || '').toLowerCase();
        if (s === 'succeeded') return 'success';
        if (s === 'failed') return 'warn';
        if (s === 'queued') return 'neutral';
        return 'neutral';
    }

    formatDuration(run: ConnectRun): string {
        const ms = run.duration_ms;
        if (typeof ms === 'number' && Number.isFinite(ms) && ms >= 0) return this.humanizeMs(ms);
        if (run.started_at && run.finished_at) {
            const started = new Date(run.started_at).getTime();
            const finished = new Date(run.finished_at).getTime();
            const delta = finished - started;
            if (!Number.isNaN(delta) && delta >= 0) return this.humanizeMs(delta);
        }
        return '-';
    }

    private humanizeMs(ms: number): string {
        if (ms < 1000) return `${Math.round(ms)}ms`;
        const totalSeconds = Math.round(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes === 0) return `${seconds}s`;
        return `${minutes}m ${seconds}s`;
    }
}
